import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { VendaFiltroRequest } from '../../requests/vendas/venda-filtro.request';
import { GetVendaKanbanResponse } from '../../responses/vendas/get-venda-kanban.response';
import { GetVendaKanbanEtapaResponse } from '../../responses/vendas/get-venda-kanban-etapa.response';
import { GetVendaListaResponse } from '../../responses/vendas/get-venda-lista.response';
import { GetVendaListaPrintResponse } from '../../responses/vendas/get-venda-lista-print.response';
import { CriarModeloVendaRequest } from '../../requests/vendas/criar-modelo-venda.request';
import { CriarVendaOcorrenciaRequest } from '../../requests/vendas/criar-venda-ocorrencia.request';
import { RegistrarImpressaoVendaRequest } from '../../requests/vendas/registrar-impressao-venda.request';
import { CompartilharVendaRequest } from '../../requests/vendas/compartilhar-venda.request';
import { CompartilharVendaResponse } from '../../responses/vendas/compartilhar-venda.response';
import { ExcluirVendaRequest } from '../../requests/vendas/excluir-venda.request';
import { ExcluirVendaResponse } from '../../responses/vendas/excluir-venda.response';
import { AlterarTagsVendaRequest } from '@shared/services/requests/vendas/alterar-tags-venda.request';
import { AlterarTagsVendaResponse } from '@shared/services/responses/vendas/alterar-tags-venda.response';
import { GetVendaHistoricoResponse } from '../../responses/vendas/get-venda-historico.response';
import { ReturnStruct } from '../../structs/return.struct';
import { CriarModeloVendaResponse } from '../../responses/vendas/criar-modelo-venda.response';
import { MoverVendaEtapaRequest } from '../../requests/vendas/mover-venda-etapa.request';
import { MoverVendaEtapaResponse } from '../../responses/vendas/mover-venda-etapa.response';
import { VincularOportunidadeVendaRequest } from '@shared/services/requests/vendas/vincular-oportunidade-venda.request';
import { CriarOportunidadeVendaRequest } from '@shared/services/requests/vendas/criar-oportunidade-venda.request';
import { CriarOportunidadeVendaResponse } from '@shared/services/responses/vendas/criar-oportunidade-venda.response';
import { GetOportunidadesAbertasClienteResponse } from '@shared/services/responses/vendas/get-oportunidades-abertas-cliente.response';
import { LancarFinanceiroVendaRequest } from '../../requests/vendas/lancar-financeiro-venda.request';
import { ExcluirFinanceiroVendaRequest } from '../../requests/vendas/excluir-financeiro-venda.request';
import { FinanceiroVendaResponse } from '../../responses/vendas/financeiro-venda.response';
import { GetFinanceiroVendaResponse } from '../../responses/vendas/get-financeiro-venda.response';
import { ExcluirEstoqueVendaRequest } from '@shared/services/requests/vendas/excluir-estoque-venda.request';
import { LancarEstoqueVendaRequest } from '@shared/services/requests/vendas/lancar-estoque-venda.request';
import { EstoqueVendaResponse } from '@shared/services/responses/vendas/estoque-venda.response';
import { GetEstoqueVendaResponse } from '@shared/services/responses/vendas/get-estoque-venda.response';

/**
 * Processo de Venda — board e visao em lista.
 *
 * Aponta para o ORQUESTRADOR, nunca para a urlApiVendas direto: e ele quem resolve o escopo
 * de visualizacao do vendedor e preenche os nomes de cliente e vendedor, que vivem em outro
 * microsservico.
 */
@Injectable({
    providedIn: 'root',
})
export class ProcessoVendaService extends CommonService {
    private readonly apiUrl: string = environment.urlApiOrquestradorVendas + 'ProcessoVenda';

    constructor(private httpClient: HttpClient) {
        super();
    }

    /**
     * Monta os filtros da toolbar, omitindo o que nao foi informado. Listas viram parametros
     * repetidos na URL (idsTagVenda=1&idsTagVenda=2), que e como o backend as recebe.
     */
    private montarParams(filtro?: VendaFiltroRequest): HttpParams {
        let params = new HttpParams();

        if (!filtro) return params;

        if (filtro.pesquisa) params = params.set('pesquisa', filtro.pesquisa);
        if (filtro.idTipoProcessoKanban != null)
            params = params.set('idTipoProcessoKanban', filtro.idTipoProcessoKanban.toString());
        if (filtro.idOrigem != null) params = params.set('idOrigem', filtro.idOrigem.toString());
        if (filtro.dataInicial) params = params.set('dataInicial', filtro.dataInicial);
        if (filtro.dataFinal) params = params.set('dataFinal', filtro.dataFinal);
        if (filtro.isMostrarModelos) params = params.set('isMostrarModelos', 'true');

        for (const idVendedor of filtro.idsVendedor ?? []) {
            params = params.append('idsVendedor', idVendedor.toString());
        }

        for (const idTag of filtro.idsTagVenda ?? []) {
            params = params.append('idsTagVenda', idTag.toString());
        }

        return params;
    }

    /** Board completo: configuracao, colunas, primeira pagina de cards e totais. */
    public getKanban(filtro?: VendaFiltroRequest): Observable<GetVendaKanbanResponse> {
        const params = this.montarParams(filtro);

        return this.httpClient
            .get<GetVendaKanbanResponse>(`${this.apiUrl}/kanban`, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    /** "Ver mais" de UMA coluna — nao refaz o quadro inteiro. */
    public getKanbanEtapa(
        idEtapaKanban: number,
        filtro?: VendaFiltroRequest,
        pagina = 0,
    ): Observable<GetVendaKanbanEtapaResponse> {
        const params = this.montarParams(filtro)
            .set('idEtapaKanban', idEtapaKanban.toString())
            .set('pagina', pagina.toString());

        return this.httpClient
            .get<GetVendaKanbanEtapaResponse>(`${this.apiUrl}/kanban/etapa`, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    /** Visao em lista, paginada. Traz tambem o estoque e o financeiro lancados de cada documento. */
    public getLista(filtro?: VendaFiltroRequest, pagina = 0): Observable<GetVendaListaResponse> {
        const params = this.montarParams(filtro)
            .set('pagina', pagina.toString());

        return this.httpClient
            .get<GetVendaListaResponse>(`${this.apiUrl}/lista`, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    /**
     * Move um documento para outra etapa.
     *
     * A tela valida antes de chamar, para nao deixar o usuario arrastar para onde nao pode — mas
     * quem decide de verdade e o servidor. Erro de regra volta em isError/errorDescription, e a
     * tela desfaz o movimento com a origem que vem na resposta.
     */
    public moverEtapa(request: MoverVendaEtapaRequest): Observable<MoverVendaEtapaResponse> {
        return this.httpClient
            .post<MoverVendaEtapaResponse>(`${this.apiUrl}/etapa`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Cria um modelo a partir de um orcamento.
     *
     * A resposta traz o card do modelo ja montado, para a tela inserir na coluna Modelo sem
     * recarregar o board inteiro.
     */
    public criarModelo(request: CriarModeloVendaRequest): Observable<CriarModeloVendaResponse> {
        return this.httpClient
            .post<CriarModeloVendaResponse>(`${this.apiUrl}/modelo`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Ocorrências do documento (histórico), por data do fato desc.
     *
     * Sem paginação: o histórico de UM documento é limitado por natureza, e cortar em páginas
     * esconderia o começo da história, que é o que se procura ali.
     */
    public getHistorico(idVenda: number): Observable<GetVendaHistoricoResponse> {
        return this.httpClient
            .get<GetVendaHistoricoResponse>(`${this.apiUrl}/idVenda/${idVenda}/historico`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Grava a anotação do usuário no histórico do documento (tipo Anotação, origem Usuário), em qualquer etapa. */
    public criarOcorrencia(idVenda: number, request: CriarVendaOcorrenciaRequest): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/idVenda/${idVenda}/ocorrencia`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Exclui uma anotação do histórico. O servidor recusa os demais tipos e a exclusão bloqueada
     * nas configurações do Pedido de Venda; a recusa volta em isError.
     */
    public excluirOcorrencia(idVenda: number, idVendaOcorrencia: number): Observable<ReturnStruct> {
        return this.httpClient
            .delete<ReturnStruct>(`${this.apiUrl}/idVenda/${idVenda}/ocorrencia/${idVendaOcorrencia}`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Lança o financeiro do pedido no Finanças (títulos e movimentações conforme a forma e a conta de cada
     * recebimento). O servidor valida tudo de novo e registra a ocorrência; boleto é best-effort e volta no resumo.
     */
    public lancarFinanceiro(idVenda: number, request: LancarFinanceiroVendaRequest): Observable<FinanceiroVendaResponse> {
        return this.httpClient
            .post<FinanceiroVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/financeiro/lancar`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Exclui o financeiro do pedido (tudo ou nada: parcela recebida, agrupada, conciliada ou período fechado recusam). */
    public excluirFinanceiro(idVenda: number, request: ExcluirFinanceiroVendaRequest): Observable<FinanceiroVendaResponse> {
        return this.httpClient
            .post<FinanceiroVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/financeiro/excluir`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Financeiro ativo do pedido no Finanças, com o motivo de bloqueio por item (diálogo de excluir). */
    public getFinanceiro(idVenda: number): Observable<GetFinanceiroVendaResponse> {
        return this.httpClient
            .get<GetFinanceiroVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/financeiro`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Lança a saída de estoque do pedido no Produto. O servidor monta as linhas a partir do pedido (local, lote e série do
     * item, da reserva ou da sugestão); o request leva o que o usuário completou no diálogo. Tudo ou nada; linha com
     * pendência recusa e volta em `pendencias`.
     */
    public lancarEstoque(idVenda: number, request: LancarEstoqueVendaRequest): Observable<EstoqueVendaResponse> {
        return this.httpClient
            .post<EstoqueVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/estoque/lancar`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Exclui o estoque do pedido no Produto (tudo ou nada: saldo que ficaria negativo recusa). */
    public excluirEstoque(idVenda: number, request: ExcluirEstoqueVendaRequest): Observable<EstoqueVendaResponse> {
        return this.httpClient
            .post<EstoqueVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/estoque/excluir`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Estoque do pedido: a prévia do que sai e as pendências (não lançado) ou as movimentações (lançado). Com a configuração
     * "Escolher na venda", `idTipoLancamentoEstoqueFabricado` (2 produto fabricado, 3 componentes) muda o que sai do fabricado.
     */
    public getEstoque(idVenda: number, idTipoLancamentoEstoqueFabricado: number | null = null): Observable<GetEstoqueVendaResponse> {
        let params = new HttpParams();
        if (idTipoLancamentoEstoqueFabricado != null) {
            params = params.set('idTipoLancamentoEstoqueFabricado', idTipoLancamentoEstoqueFabricado);
        }

        return this.httpClient
            .get<GetEstoqueVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/estoque`, { params, ...this.addHeaderToken() })
            .pipe(catchError(this.handleError));
    }

    /** Grava a ocorrência de Impressão no histórico (o pedido exige o layout; o orçamento não). */
    public registrarImpressao(idVenda: number, request: RegistrarImpressaoVendaRequest): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/idVenda/${idVenda}/ocorrencia/impressao`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Gera o link público do documento e envia o e-mail. Gerar de novo revoga o link vigente. Se o
     * e-mail falhar, o link já existe: a resposta volta com isError e `url` preenchida.
     */
    public compartilhar(idVenda: number, request: CompartilharVendaRequest): Observable<CompartilharVendaResponse> {
        return this.httpClient
            .post<CompartilharVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/link-publico`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Revoga o link público vigente: a página pública passa a responder "indisponível". */
    public revogarLinkPublico(idVenda: number): Observable<ReturnStruct> {
        return this.httpClient
            .delete<ReturnStruct>(`${this.apiUrl}/idVenda/${idVenda}/link-publico`, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * Exclusao logica de um ou mais documentos.
     *
     * A resposta e POR DOCUMENTO: o lote e parcial por natureza, e cada recusa vem com o motivo.
     * Nao confundir com `isError`, que e falha da operacao inteira.
     */
    public excluir(request: ExcluirVendaRequest): Observable<ExcluirVendaResponse> {
        return this.httpClient
            .post<ExcluirVendaResponse>(`${this.apiUrl}/excluir`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Tags em lote nos documentos selecionados da Lista (POST ProcessoVenda/tags). */
    public alterarTags(request: AlterarTagsVendaRequest): Observable<AlterarTagsVendaResponse> {
        return this.httpClient
            .post<AlterarTagsVendaResponse>(`${this.apiUrl}/tags`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * A listagem inteira, sem paginacao — para a impressao.
     *
     * Mesmos filtros do getLista: imprimir e levar ao papel o que esta na tela, nao outra consulta.
     * O servidor corta num teto e marca `isTruncado`, para a tela avisar em vez de imprimir uma
     * parte como se fosse o todo.
     */
    public getListaPrint(filtro: VendaFiltroRequest): Observable<GetVendaListaPrintResponse> {
        return this.httpClient
            .get<GetVendaListaPrintResponse>(`${this.apiUrl}/lista/print`, {
                params: this.montarParams(filtro),
                ...this.addHeaderToken(),
            })
            .pipe(catchError(this.handleError));
    }

    /**
     * Vincula o documento a uma oportunidade em andamento do mesmo cliente. Escopo, situação e cliente da
     * oportunidade são conferidos pelo orquestrador; a recusa volta em isError com o texto da API.
     */
    public vincularOportunidade(idVenda: number, idOportunidade: number): Observable<ReturnStruct> {
        const request = new VincularOportunidadeVendaRequest();
        request.idOportunidade = idOportunidade;

        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/idVenda/${idVenda}/oportunidade/vincular`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Desvincula o documento da oportunidade; o documento e a oportunidade continuam, sem alteração. */
    public desvincularOportunidade(idVenda: number): Observable<ReturnStruct> {
        return this.httpClient
            .post<ReturnStruct>(`${this.apiUrl}/idVenda/${idVenda}/oportunidade/desvincular`, {}, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /**
     * "Criar oportunidade" pelo documento: o orquestrador cria no CRM, com o cliente do documento e a primeira
     * etapa, e vincula. Se o vínculo falhar, volta isError com o id e o código da oportunidade criada.
     */
    public criarOportunidadeDoDocumento(
        idVenda: number,
        request: CriarOportunidadeVendaRequest,
    ): Observable<CriarOportunidadeVendaResponse> {
        return this.httpClient
            .post<CriarOportunidadeVendaResponse>(`${this.apiUrl}/idVenda/${idVenda}/oportunidade/nova`, request, this.addHeaderToken())
            .pipe(catchError(this.handleError));
    }

    /** Oportunidades em andamento do cliente no CRM, dentro do escopo do vendedor (opções do "Vincular"). */
    public getOportunidadesAbertasDoCliente(idPessoa: number): Observable<GetOportunidadesAbertasClienteResponse> {
        return this.httpClient
            .get<GetOportunidadesAbertasClienteResponse>(
                `${this.apiUrl}/cliente/idPessoa/${idPessoa}/oportunidades-abertas`,
                this.addHeaderToken(),
            )
            .pipe(catchError(this.handleError));
    }
}
